/**
 * DPGen (`dpgen:v1`) — constantes do algoritmo.
 *
 * ATENÇÃO: tudo neste arquivo faz parte do contrato de reprodutibilidade.
 * Mudar qualquer valor aqui muda TODAS as senhas já geradas. Se um dia for
 * necessário evoluir o algoritmo, crie `dpgen:v2` em paralelo e deixe o v1
 * intacto — nunca edite o v1.
 *
 * A especificação completa está em `spec/dpgen-v1.md`.
 */

/** Domínio de separação. Entra no salt e nos rótulos derivados. */
export const ALGO_VERSION = 'dpgen:v1'

/** Separador entre campos do salt (ASCII Unit Separator — nunca aparece nas entradas). */
export const FIELD_SEPARATOR = '\x1f'

/** Bytes pedidos ao Argon2id. 1024 bits cobrem com folga o maior comprimento. */
export const HASH_LENGTH = 128

export const MIN_LENGTH = 6
export const MAX_LENGTH = 64
export const MIN_COUNTER = 1
export const MAX_COUNTER = 9_999_999

// ---------------------------------------------------------------------------
// Conjuntos de caracteres
// ---------------------------------------------------------------------------

export const LOWER = 'abcdefghijklmnopqrstuvwxyz'
export const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
export const DIGITS = '0123456789'

/** Símbolos aceitos por praticamente todo site: sem aspas, barra invertida ou espaço. */
export const SYMBOLS_SAFE = '!#$%&()*+,-./:;<=>?@[]^_{|}~'

/** Símbolos que alguns formulários rejeitam ou escapam de forma inconsistente. */
export const SYMBOLS_EXTRA = '"\'`\\ '

export const SYMBOLS_EXTENDED = SYMBOLS_SAFE + SYMBOLS_EXTRA

/** Caracteres fáceis de confundir ao ler ou ditar uma senha. */
export const AMBIGUOUS = 'O0oIl1|'

export type SymbolSet = 'safe' | 'extended'

export interface Policy {
  /** Número de caracteres da senha final. */
  length: number
  lower: boolean
  upper: boolean
  digits: boolean
  symbols: boolean
  symbolSet: SymbolSet
  excludeAmbiguous: boolean
}

// ---------------------------------------------------------------------------
// Perfis de custo do Argon2id
// ---------------------------------------------------------------------------

export type CostProfileId = 'fast' | 'balanced' | 'high'

export interface CostProfile {
  id: CostProfileId
  /** Memória em KiB. */
  memorySize: number
  iterations: number
  parallelism: number
}

/**
 * O perfil de custo altera a saída do Argon2id, logo faz parte da "receita":
 * a mesma senha-mestra com perfis diferentes gera senhas diferentes.
 */
export const COST_PROFILES: Record<CostProfileId, CostProfile> = {
  fast: { id: 'fast', memorySize: 32 * 1024, iterations: 2, parallelism: 1 },
  balanced: { id: 'balanced', memorySize: 64 * 1024, iterations: 3, parallelism: 1 },
  high: { id: 'high', memorySize: 256 * 1024, iterations: 4, parallelism: 1 },
}

export const DEFAULT_COST_PROFILE: CostProfileId = 'balanced'

// ---------------------------------------------------------------------------
// Entradas completas
// ---------------------------------------------------------------------------

export interface DerivationInput {
  master: string
  service: string
  login: string
  counter: number
  policy: Policy
  cost: CostProfileId
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export type PresetId = 'compat' | 'standard' | 'long' | 'pin' | 'lowercase'

export const PRESETS: Record<PresetId, Policy> = {
  /** Para sites antigos que recusam símbolos ou limitam o comprimento. */
  compat: {
    length: 12,
    lower: true,
    upper: true,
    digits: true,
    symbols: false,
    symbolSet: 'safe',
    excludeAmbiguous: true,
  },
  standard: {
    length: 16,
    lower: true,
    upper: true,
    digits: true,
    symbols: true,
    symbolSet: 'safe',
    excludeAmbiguous: false,
  },
  long: {
    length: 32,
    lower: true,
    upper: true,
    digits: true,
    symbols: true,
    symbolSet: 'safe',
    excludeAmbiguous: false,
  },
  /** PIN numérico para cofres, cartões e telas de bloqueio. */
  pin: {
    length: 6,
    lower: false,
    upper: false,
    digits: true,
    symbols: false,
    symbolSet: 'safe',
    excludeAmbiguous: false,
  },
  /** Fácil de digitar em TV, console e teclados virtuais ruins. */
  lowercase: {
    length: 20,
    lower: true,
    upper: false,
    digits: false,
    symbols: false,
    symbolSet: 'safe',
    excludeAmbiguous: false,
  },
}

export const PRESET_ORDER: PresetId[] = ['compat', 'standard', 'long', 'pin', 'lowercase']

export const DEFAULT_POLICY: Policy = { ...PRESETS.standard }

/** Retorna o preset equivalente à política, ou null se ela for personalizada. */
export function matchPreset(policy: Policy): PresetId | null {
  for (const id of PRESET_ORDER) {
    const p = PRESETS[id]
    if (
      p.length === policy.length &&
      p.lower === policy.lower &&
      p.upper === policy.upper &&
      p.digits === policy.digits &&
      p.symbols === policy.symbols &&
      p.excludeAmbiguous === policy.excludeAmbiguous &&
      // O conjunto de símbolos só diferencia presets quando símbolos estão ligados.
      (!p.symbols || p.symbolSet === policy.symbolSet)
    ) {
      return id
    }
  }
  return null
}
